/*
 * Logger personalizzato dell'applicazione.
 *
 * Fornisce un'interfaccia compatibile con il Logger di matrix-js-sdk,
 * permettendo la sostituzione diretta (drop-in replacement).
 *
 * Caratteristiche:
 * - Prefisso gerarchico tramite getChild() (es. "Modulo.Sottomodulo")
 * - Filtraggio per livello minimo, configurabile per singola istanza
 * - Fallback automatico al livello globale del ConsoleInterceptor
 * - Metodo log() deprecato per retrocompatibilità (delega a debug)
 *
 * Nota sul doppio filtraggio:
 * Il logger controlla il livello PRIMA di chiamare console.*, e il
 * ConsoleInterceptor può filtrare ulteriormente a livello globale.
 * Questo è intenzionale: il livello globale agisce come soglia minima
 * invalicabile, anche se un'istanza specifica ha un livello più permissivo.
 */

import { LogLevel, isLevelEnabled } from "./LogLevel";
import { getGlobalMinLevel } from "./ConsoleInterceptor";

// ─── Tipi di metodo console utilizzati ──────────────────────────────────────

/** Nomi dei metodi console supportati dal logger. */
type ConsoleMethodName = "trace" | "debug" | "info" | "warn" | "error";

// ─── Interfaccia pubblica ───────────────────────────────────────────────────

/**
 * Interfaccia del logger dell'applicazione.
 * Compatibile con l'interfaccia Logger di matrix-js-sdk
 * per garantire la sostituibilità diretta.
 */
export interface ILogger {
    trace(...msg: unknown[]): void;
    debug(...msg: unknown[]): void;
    info(...msg: unknown[]): void;
    warn(...msg: unknown[]): void;
    error(...msg: unknown[]): void;
    /** @deprecated Usa debug() al suo posto. */
    log(...msg: unknown[]): void;
    getChild(namespace: string): ILogger;
}

// ─── Implementazione ────────────────────────────────────────────────────────

/**
 * Implementazione concreta del logger dell'applicazione.
 *
 * Ogni istanza può avere:
 * - Un prefisso (aggiunto automaticamente a ogni messaggio)
 * - Un livello minimo proprio (overrideLevel), che ha priorità sul livello globale
 */
class AppLogger implements ILogger {
    /**
     * @param prefix - Prefisso opzionale preposto a ogni messaggio tra parentesi quadre.
     *                 I child logger concatenano i prefissi con "." come separatore.
     * @param overrideLevel - Livello minimo specifico per questa istanza.
     *                        Se non specificato, viene usato il livello globale.
     */
    public constructor(
        private readonly prefix?: string,
        private overrideLevel?: LogLevel,
    ) {}

    /**
     * Imposta il livello minimo di log per questa specifica istanza.
     * Ha priorità sul livello globale configurato nel ConsoleInterceptor.
     */
    public setLevel(level: LogLevel): void {
        this.overrideLevel = level;
    }

    /**
     * Restituisce il livello minimo effettivo per questa istanza:
     * quello specifico dell'istanza se definito, altrimenti il livello globale.
     */
    public getEffectiveLevel(): LogLevel {
        return this.overrideLevel ?? getGlobalMinLevel();
    }

    // ─── Metodi di log pubblici ─────────────────────────────────────────

    public trace(...msg: unknown[]): void {
        this.emit("trace", LogLevel.Debug, msg);
    }

    public debug(...msg: unknown[]): void {
        this.emit("debug", LogLevel.Debug, msg);
    }

    public info(...msg: unknown[]): void {
        this.emit("info", LogLevel.Info, msg);
    }

    public warn(...msg: unknown[]): void {
        this.emit("warn", LogLevel.Warn, msg);
    }

    public error(...msg: unknown[]): void {
        this.emit("error", LogLevel.Error, msg);
    }

    /** @deprecated Usa debug() al suo posto. */
    public log(...msg: unknown[]): void {
        this.debug(...msg);
    }

    /**
     * Crea un logger figlio con un namespace aggiuntivo.
     *
     * Il prefisso del figlio è composto concatenando il prefisso del padre
     * e il namespace con "." come separatore.
     * Il figlio eredita il livello di override del padre, se presente.
     *
     * Esempio:
     *   const child = logger.getChild("Auth");
     *   child.info("Login riuscito");
     *   // Output: [Auth] Login riuscito
     *
     *   const grandchild = child.getChild("OAuth");
     *   grandchild.warn("Token scaduto");
     *   // Output: [Auth.OAuth] Token scaduto
     */
    public getChild(namespace: string): ILogger {
        const childPrefix = this.prefix ? `${this.prefix}.${namespace}` : namespace;
        return new AppLogger(childPrefix, this.overrideLevel);
    }

    // ─── Metodi interni ─────────────────────────────────────────────────

    /**
     * Verifica se un messaggio al livello specificato dovrebbe essere emesso
     * da questa istanza (confronto con il livello effettivo).
     */
    private isEnabled(level: LogLevel): boolean {
        return isLevelEnabled(level, this.getEffectiveLevel());
    }

    /**
     * Metodo centralizzato per l'emissione dei messaggi.
     *
     * Flusso:
     *  1. Controlla se il livello è abilitato per questa istanza → esce subito se no
     *  2. Aggiunge il prefisso tra parentesi quadre se presente
     *  3. Delega al metodo console.* corrispondente
     *
     * Il metodo console viene risolto tramite nome (stringa) per garantire
     * che venga sempre chiamato con il contesto corretto (`console` come this),
     * anche dopo l'intercettazione da parte del ConsoleInterceptor.
     */
    private emit(method: ConsoleMethodName, level: LogLevel, msg: unknown[]): void {
        if (!this.isEnabled(level)) return;

        if (this.prefix) {
            console[method](`[${this.prefix}]`, ...msg);
        } else {
            console[method](...msg);
        }
    }
}

// ─── Istanza singleton ──────────────────────────────────────────────────────

/**
 * Logger radice dell'applicazione.
 *
 * Usare getChild() per creare logger specifici per namespace:
 *   import { logger } from "../logger";
 *   const myLogger = logger.getChild("MioModulo");
 *   myLogger.info("Inizializzazione completata");
 */
export const logger: ILogger = new AppLogger();
